/**
 * How an amount reads on screen, how it is typed, and what travels on the wire.
 * Every managed lead write path that carries a figure - the stage-owned
 * advancement and the request values correction - reads these three shapes from
 * here: a second copy of the grouping or of the decimal separator could drift
 * while describing the same amount.
 */

/** Italian thousands separators, applied to the integer part of an amount. */
const groupThousands = (integer: string) =>
  integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/** A canonical amount, and nothing else: digits with an optional dot fraction. */
const CANONICAL_AMOUNT_PATTERN = /^\d+(\.\d+)?$/;

/**
 * Italian reading of a canonical amount: `25000.5` becomes `25.000,5`. The
 * decimals are left as they arrived - trimming them here would hide a value the
 * operator has to be able to see and correct. A value that is not an amount at
 * all is handed back untouched: what HubSpot holds has to stay visible even
 * when the backoffice did not produce it.
 */
export const managedLeadCurrencyToDisplay = (canonical: string) => {
  if (!canonical) return '';

  const [integer, decimals] = canonical.split('.');
  return decimals === undefined
    ? groupThousands(integer)
    : `${groupThousands(integer)},${decimals}`;
};

/**
 * Regroups an amount as it is typed. Anything that is not a digit or a decimal
 * comma is dropped, but the decimals themselves are kept whole: truncating them
 * here would silently accept a precision the contract rejects.
 */
export const managedLeadCurrencyInput = (raw: string) => {
  const [integer, ...decimals] = raw.replace(/[^\d,]/g, '').split(',');
  return decimals.length
    ? `${groupThousands(integer)},${decimals.join('')}`
    : groupThousands(integer);
};

/** The wire form of a typed amount: dot separator, no grouping. */
export const managedLeadCurrencyToCanonical = (display: string) =>
  display.replace(/\./g, '').replace(',', '.');

/**
 * The single decimal string that stands for an amount, or `null` when the text
 * is not one. `12000`, `12000.0` and `12000.00` are the same figure, so a
 * comparison that reads them as different would report a correction where
 * nothing changed. It mirrors the canonicalisation the server applies before it
 * looks for a delta, which is what makes the two agree on "unchanged".
 */
export const canonicalManagedLeadAmount = (value: string) => {
  if (!CANONICAL_AMOUNT_PATTERN.test(value)) return null;

  const [integer, fraction = ''] = value.split('.');
  const digits = integer.replace(/^0+(?=\d)/, '');
  const decimals = fraction.replace(/0+$/, '');
  return decimals ? `${digits}.${decimals}` : digits;
};
